/**
 * PostHog Event Sanitization
 * Prevents PII/PHI and sensitive data leakage to PostHog
 * Reuses patterns from sentry-sanitization for consistency
 */

import { redactSensitiveFields, redactString } from '../utilis/sentry-sanitization';

// URL params that may contain sensitive IDs - redact their values
const SENSITIVE_URL_PARAMS = [
  'accountid',
  'facilityid',
  'patientid',
  'userid',
  'token',
  'auth',
  'key',
];

/**
 * Sanitizes a URL by redacting sensitive query parameters
 * Handles both standard URLs and hash-based routing (e.g. #/dashboard?accountId=xxx)
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    // Hash routing: url may be "#/path?accountId=xxx" or "https://x.com/#/path?accountId=xxx"
    const hashIdx = url.indexOf('#');
    const beforeHash = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
    const hashPart = hashIdx >= 0 ? url.slice(hashIdx) : '';

    let path = beforeHash;
    let search = '';

    if (hashPart) {
      const qIdx = hashPart.indexOf('?');
      if (qIdx >= 0) {
        path = beforeHash + hashPart.slice(0, qIdx);
        search = hashPart.slice(qIdx + 1);
      }
    } else {
      const qIdx = url.indexOf('?');
      if (qIdx >= 0) {
        path = url.slice(0, qIdx);
        search = url.slice(qIdx + 1);
      }
    }

    if (!search) return redactString(path);

    const params = new URLSearchParams(search);
    const sanitizedParams = new URLSearchParams();
    for (const [key, value] of params) {
      const lowerKey = key.toLowerCase();
      const isSensitive = SENSITIVE_URL_PARAMS.some((p) => lowerKey === p.toLowerCase());
      sanitizedParams.set(key, isSensitive ? '[REDACTED]' : redactString(value));
    }
    const newSearch = sanitizedParams.toString();
    return newSearch ? `${redactString(path)}?${newSearch}` : redactString(path);
  } catch {
    return '[REDACTED]';
  }
}

/**
 * Sanitizes PostHog event before sending - removes/replace sensitive data
 * @param {Object} event - PostHog event object { event, properties, $set, $set_once, ... }
 * @returns {Object|null} Sanitized event or null to drop
 */
export function sanitizePostHogEvent(event) {
  if (!event || typeof event !== 'object') return event;

  try {
    const sanitized = { ...event };
    const properties = { ...(sanitized.properties || {}) };

    // Sanitize URL-related properties (pageview, current_url, pageleave, etc.)
    const urlProps = ['$current_url', '$pathname', '$initial_pathname', 'pathname', 'url', '$prev_pathname'];
    urlProps.forEach((prop) => {
      if (properties[prop]) {
        properties[prop] = sanitizeUrl(String(properties[prop]));
      }
    });

    // Sanitize $exception event properties (error messages/stack may contain PHI)
    if (event.event === '$exception') {
      ['$exception_message', '$exception_value', '$exception_type', '$exception_stack_trace_raw', '$exception_stack_trace'].forEach((prop) => {
        if (properties[prop] && typeof properties[prop] === 'string') {
          properties[prop] = redactString(properties[prop]);
        }
      });
    }

    // Sanitize person properties ($set, $set_once) - may contain PII if set elsewhere
    ['$set', '$set_once'].forEach((key) => {
      if (sanitized[key] && typeof sanitized[key] === 'object') {
        sanitized[key] = redactSensitiveFields(sanitized[key]);
      }
    });
    if (properties.$set) properties.$set = redactSensitiveFields(properties.$set);
    if (properties.$set_once) properties.$set_once = redactSensitiveFields(properties.$set_once);

    // Recursively sanitize all properties for known sensitive keys
    sanitized.properties = redactSensitiveFields(properties);

    return sanitized;
  } catch {
    // If sanitization fails, return null to drop (safer than sending unsanitized data)
    return null;
  }
}
